<template>
  <div>
    <div class="grid grid-cols-1 gap-4 max-h-[60vh] overflow-y-auto">
      <!-- Header Information -->
      <div class="grid grid-cols-2 px-4 gap-4 mb-6">
        <div>
          <p class="font-semibold">Date & Time reviewed and published:</p>
          <p>{{ formattedTimestamp(firstTest?.updated_at) }}</p>
        </div>
        <div>
          <p class="font-semibold">Results sent to:</p>
          <p>{{ firstTest?.requested_by?.Username }}</p>
        </div>
      </div>

      <!-- Test Sections -->
      <div v-for="(test, index) in filteredRequests" :key="index" class="mb-8">
        <h2 class="text-lg font-bold mb-4">{{ test.test_type?.name }}</h2>
        <div class="grid grid-cols-2 px-4 gap-4 mb-6">
          <div>
            <p class="font-semibold">Requesting Doctor</p>
            <p>{{ test.requested_by?.Username }}</p>
          </div>
          <div>
            <p class="font-semibold">Reviewer/Lab Technician</p>
            <p>{{ test.lab_results[0]?.filled_by?.Username }}</p>
          </div>
          <div>
            <p class="font-semibold">Test Results</p>
            <div v-if="parseAnalyteBlob(test.lab_results[0]?.results)" class="space-y-0.5 mt-1">
              <div v-for="(entry, name) in parseAnalyteBlob(test.lab_results[0]?.results)" :key="name" class="text-sm">
                <span class="font-medium text-gray-700">{{ name }}:</span>
                <span :class="entry.flag === 'H' || entry.flag === 'L' ? 'font-semibold text-red-700' : 'text-gray-800'">{{ entry.value }}</span>
                <span v-if="entry.unit" class="text-gray-400">{{ entry.unit }}</span>
                <span v-if="entry.flag === 'H' || entry.flag === 'L'" class="ml-1 px-1.5 py-0.5 text-[10px] font-bold rounded bg-red-100 text-red-700">{{ entry.flag }}</span>
              </div>
            </div>
            <p v-else>{{ test.lab_results[0]?.results }}</p>
          </div>
          <div>
            <p class="font-semibold">Attached Reports:</p>
            <div class="mt-2 p-2 border rounded-lg bg-gray-50">
              <div
                v-for="(file, fileIndex) in test.lab_results[0]?.url"
                :key="fileIndex"
                @click="downloadFile(file.url)"
                class="flex justify-between items-center mb-2"
              >
                <div class="flex items-center gap-2">
                  <span class="text-red-500">{{ file.filename?.split('.').pop().toUpperCase() }}</span>
                  <span>{{ file.filename || 'Lab Report' }}</span>
                </div>
                <button
                  class="bg-gray-400 text-white px-4 py-2 rounded hover:bg-gray-500 transition duration-300 flex items-center"
                >
                  <svg
                    class="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                    />
                  </svg>
                </button>
              </div>
              <div v-if="!test.lab_results[0]?.url || test.lab_results[0]?.url.length === 0">No Attachment</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "SendResultsTab",
  props: {
    requests: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      fileName: "",
      labForm: {
        fileName: "",
      },
      tests: [],
      firstTest: null,
    };
  },
  computed: {
    filteredRequests() {
      const completeTests = this.requests.filter(item => item.status?.code === "ST-013");
      if (completeTests.length > 0) {
        this.firstTestDetails(completeTests[0]);
      }
      return completeTests;
    },
  },
  mounted() {
    this.$emit("mounted", "SendResultsTab");
  },
  methods: {
    // Structured lab capture stores results as a JSON blob (`{ "Analyte": { value, unit, flag } }`);
    // older free-text results aren't JSON, so this returns null and the raw string is shown instead.
    parseAnalyteBlob(resultString) {
      if (!resultString || typeof resultString !== 'string') return null;
      try {
        const parsed = JSON.parse(resultString);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed) && Object.keys(parsed).length) {
          return parsed;
        }
        return null;
      } catch {
        return null;
      }
    },

    formattedTimestamp(dateString) {
      const options = {
        weekday: 'long', // "Friday", "Monday"
        year: "numeric", // "2025"
        month: "long", // "January"
        day: "numeric", // "22"
        hour: "numeric", // "1 PM"
        minute: "numeric", // "43"
        hour12: true, // 12-hour time format
      };

      // parse as UTC
      const utcDate = new Date(dateString);

      const date = new Date(utcDate.getTime());

      return date.toLocaleString("en-US", options);
    },
    firstTestDetails(test) {
      this.firstTest = test;
    },
    async downloadFile(fileUrl) {
      console.log("Downloading file:", fileUrl);
      try {
        const response = await axios.get(fileUrl, { responseType: "blob" });
        let fileName = fileUrl.split("/").pop().split("?")[0];
        const contentDisposition = response.headers["content-disposition"];
        if (contentDisposition) {
          const match = contentDisposition.match(/filename="?([^"]+)"?/);
          if (match) fileName = match[1];
        }
        const blob = new Blob([response.data]);
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error("Error downloading file:", error);
        this.$toast.error("Failed to download file.");
      }
    },
  },
};
</script>

<style lang="scss" scoped></style>